import { EQuestionType } from '@constants';
import { IQuestionsFilters } from '@sdk/contracts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { create as createStore } from 'zustand';

import { sdk } from '@/services';

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 50;

const QuestionService = sdk.getService('QuestionService');

const ALL_QUESTIONS_FILTER = {
  type: [EQuestionType.AI, EQuestionType.MANUAL]
};

interface IStore {
  filter: IQuestionsFilters;
  search: string;
  setSearch: (search: string) => void;
  setFilter: (filter: IQuestionsFilters) => void;
}

const store = createStore<IStore>(set => ({
  filter: ALL_QUESTIONS_FILTER,
  search: '',
  setSearch: (search: string) => set(state => ({ ...state, search, page: INITIAL_PAGE })),
  setFilter: (filter: IQuestionsFilters) => set(state => ({ ...state, filter, page: INITIAL_PAGE }))
}));

export const useQuestionsList = () => {
  const { filter, search, setFilter, setSearch } = store();
  const queryKey = ['questions', search, filter];

  const { data, isLoading, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = INITIAL_PAGE }) =>
      QuestionService.getQuestions({ page: pageParam, limit: INITIAL_LIMIT }, filter, search),
    getNextPageParam: lastPage => {
      if (!lastPage.hasNextPage) return undefined;
      return lastPage.page + 1;
    },
    initialPageParam: INITIAL_PAGE
  });

  const filtersCount = useMemo(
    () => (filter !== ALL_QUESTIONS_FILTER ? Object.keys(filter).length - 1 : 0),
    [filter]
  );

  const resetSearchResults = () => setSearch('');

  const resetFilters = () => setFilter(ALL_QUESTIONS_FILTER);

  return {
    search,
    setSearch,
    resetSearchResults,
    filter,
    setFilter,
    fetchNextPage,
    questions: data?.pages.flatMap(page => page.docs),
    isFetchingNextPage,
    isLoading,
    filtersCount,
    resetFilters
  };
};
